import { motion } from 'framer-motion';
import { BadgeCheck, Store, Shield } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

/**
 * Step 0 — Vendor Type selector.
 * Renders the 3 vendor-type cards, EIN field (for verified/nonprofit),
 * and the nonprofit website field.
 */
export default function Step0VendorType({
  vendorType,
  setVendorType,
  einNumber,
  setEinNumber,
  nonprofitUrl,
  setNonprofitUrl,
}) {
  return (
    <div className="space-y-6">
      <p className="text-stone-400 font-outfit text-center mb-8">
        Select your business registration status
      </p>
      <RadioGroup value={vendorType} onValueChange={setVendorType} className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <RadioGroupItem value="verified" id="verified" className="peer sr-only" />
          <Label
            htmlFor="verified"
            className="flex flex-col items-center p-6 rounded-2xl border border-stone-800 cursor-pointer peer-data-[state=checked]:border-yellow-400 peer-data-[state=checked]:bg-yellow-400/10 hover:border-stone-700 transition-colors"
            data-testid="vendor-type-verified"
          >
            <BadgeCheck className="w-12 h-12 text-turquoise-400 mb-4" />
            <span className="font-syne text-lg font-bold text-stone-200 mb-2">Verified Business</span>
            <span className="text-stone-400 text-sm text-center font-outfit">
              I have an LLC, Sole Proprietorship, or EIN
            </span>
          </Label>
        </div>
        <div>
          <RadioGroupItem value="startup" id="startup" className="peer sr-only" />
          <Label
            htmlFor="startup"
            className="flex flex-col items-center p-6 rounded-2xl border border-stone-800 cursor-pointer peer-data-[state=checked]:border-yellow-400 peer-data-[state=checked]:bg-yellow-400/10 hover:border-stone-700 transition-colors"
            data-testid="vendor-type-startup"
          >
            <Store className="w-12 h-12 text-yellow-400 mb-4" />
            <span className="font-syne text-lg font-bold text-stone-200 mb-2">Startup Business</span>
            <span className="text-stone-400 text-sm text-center font-outfit">
              Not yet legally registered, but ready to sell
            </span>
          </Label>
        </div>
        <div>
          <RadioGroupItem value="nonprofit" id="nonprofit" className="peer sr-only" />
          <Label
            htmlFor="nonprofit"
            className="flex flex-col items-center p-6 rounded-2xl border border-stone-800 cursor-pointer peer-data-[state=checked]:border-yellow-400 peer-data-[state=checked]:bg-yellow-400/10 hover:border-stone-700 transition-colors"
            data-testid="vendor-type-nonprofit"
          >
            <Shield className="w-12 h-12 text-green-400 mb-4" />
            <span className="font-syne text-lg font-bold text-stone-200 mb-2">501(c)(3) Non-Profit</span>
            <span className="text-stone-400 text-sm text-center font-outfit">
              Registered non-profit organization
            </span>
          </Label>
        </div>
      </RadioGroup>

      {(vendorType === 'verified' || vendorType === 'nonprofit') && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-6 rounded-xl border border-yellow-400/50 bg-yellow-400/5"
        >
          <Label className="text-stone-300 font-outfit font-bold">EIN Number * (Required for Verified Businesses)</Label>
          <Input
            value={einNumber}
            onChange={(e) => {
              // Format EIN as XX-XXXXXXX
              const value = e.target.value.replace(/\D/g, '').slice(0, 9);
              if (value.length > 2) {
                setEinNumber(`${value.slice(0, 2)}-${value.slice(2)}`);
              } else {
                setEinNumber(value);
              }
            }}
            placeholder="XX-XXXXXXX"
            className="input-field w-full mt-2 font-mono text-lg tracking-wider"
            data-testid="ein-number-input"
            maxLength={10}
          />
          <p className="text-yellow-400/70 text-xs mt-2 font-outfit">
            Your Employer Identification Number (EIN) issued by the IRS.
          </p>
        </motion.div>
      )}

      {vendorType === 'nonprofit' && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="p-4 rounded-xl border border-green-500/30 bg-green-500/5">
          <Label className="text-stone-300 font-outfit font-bold">Organization Website (optional)</Label>
          <Input
            value={nonprofitUrl}
            onChange={(e) => setNonprofitUrl(e.target.value)}
            placeholder="https://your-nonprofit.org"
            className="input-field w-full mt-2"
            data-testid="nonprofit-url"
          />
          <p className="text-green-400/70 text-xs mt-1 font-outfit">Your non-profit organization's website URL</p>
        </motion.div>
      )}
    </div>
  );
}
